import React, { useState, useEffect, useCallback, useRef } from "react"
import "../../styles/Pages/store.css"
import PageHeader from "../../components/Common/PageHeader"
import EmptyState from "../../components/Common/EmptyState"
import Table from "../../components/Common/Table"
import Dropdown from "../../components/Common/Dropdown"
import StoreResubmissionChat from "../../components/Stores/StoreResubmissionChat"
import { Spin, message, Select, Pagination } from "antd"
import {
  getStoreActions,
  viewOnlyStoreActions,
  getReadOnlyStoreActions,
} from "../../utils/TableData/Store/storeActionsTable"
import { getStoreColumns } from "../../utils/TableData/Store/storeColumns"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import useStoreActions from "../../services/useStore"
import { getUserCookies } from "../../utils/Cookies"
import StoreDrawer from "../../components/Common/StoreDrawer"
import StoreProductViewModal from "../../components/Common/Modals/StoreProductViewModal"
import StoreVIewDetailsModal from "../../components/Common/Modals/StoreVIewDetailsModal"
import { jwtDecode } from "jwt-decode"
import { fetchApprovedMerchants } from "../../ReduxToolKit/Slice/MerchantsSlice"
import DeleteConfirmationModal from "../../components/Common/Modals/DeleteConfirmationModal"
import { translations } from "../../utils/Translation/staticTranslation"
import {
  effectiveHighestRole,
  isProfileModuleDeleteBlocked,
  isProfileModuleReadOnly,
} from "../../utils/modulePermissionSidebar"

/** Rejected tab lists both rejected (3) and permanently rejected (5) requests. */
const REJECTED_REQUEST_STATUS = "3,5"

const Stores = () => {
  const token = getUserCookies()?.token
  const decoded = token ? jwtDecode(token) : null
  const highest_role = decoded?.highest_role
  const market_role = decoded?.market_role
  const user = useSelector((state) => state.user.user)
  const isReadOnly =
    market_role === "2" ||
    market_role === "3" ||
    isProfileModuleReadOnly(user, "stores")
  const canDelete = !isReadOnly && !isProfileModuleDeleteBlocked(user, "stores")
  const effectiveHighest = effectiveHighestRole(user, highest_role)
  /** Multi-merchant admin Store UI (profile market admin resolves to SUPERADMIN, or JWT market viewer). */
  const showAdminStoreUI =
    effectiveHighest === "SUPERADMIN" || isReadOnly
  const isInitialMount = useRef(true)
  const searchTimeout = useRef(null)
  const fetchInProgress = useRef(false)
  /** Re-run default store load when profile resolves (JWT OWNER → effective SUPERADMIN). */
  const storeRoleBootstrapKey = useRef(null)

  const navigate = useNavigate()
  const dispatch = useDispatch()

  const merchants = useSelector((state) => state.merchant.merchants)

  // State with default values
  const [selectedStatus, setSelectedStatus] = useState("approved")
  const [selectedCategory, setSelectedCategory] = useState("Name") // Default to Name
  const [filterStatus, setFilterStatus] = useState("All") // Default to All
  const [searchTerm, setSearchTerm] = useState("")
  const [drawerVisible, setDrawerVisible] = useState(false)
  const [selectMerchant, setSelectMerchant] = useState("")

  // Track defaults for each tab
  const [statusDefaults, setStatusDefaults] = useState({
    approved: { category: "Name", filter: "All" },
    pending: { category: "Name" },
    rejected: { category: "Name" },
  })

  // Modal states
  const [productModal, setProductModal] = useState(false)
  const [selectedStore, setSelectedStore] = useState({})
  const [requestModal, setRequestModal] = useState(false)
  const [deleteModalVisible, setDeleteModalVisible] = useState(false)
  const [storeToDelete, setStoreToDelete] = useState(null)
  const [isRequestDelete, setIsRequestDelete] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [rejectedRequestId, setRejectedRequestId] = useState(null)
  const [isRejectedPermanently, setIsRejectedPermanently] = useState(false)

  const {
    stores,
    setStores,
    loading,
    fetchApprovedStore,
    fetchOtherStores,
    handleDelete,
    handleDeleteRequest,
    deleteStore,
    pagination,
    setPagination,
  } = useStoreActions(setDeleteModalVisible)

  const lang = localStorage.getItem("language") || "ar"
  const currentLanguage = translations[lang]?.StorePage
  const readOnlyActionHint = translations[lang]?.Referrals?.read_only_hint
  const filters = translations[lang]?.filters
  const modal = translations[lang]?.modals

  // Prevent duplicate merchant fetch
  const hasFetchedMerchants = useRef(false)

  // Load merchants for platform / profile market admin or JWT market viewer roles
  useEffect(() => {
    if (
      showAdminStoreUI &&
      !hasFetchedMerchants.current &&
      (!merchants || merchants.length === 0)
    ) {
      hasFetchedMerchants.current = true
      dispatch(fetchApprovedMerchants())
    }
  }, [dispatch, showAdminStoreUI, merchants])

  // Bootstrap list when role context changes (fixes empty table after profile loads for market admins)
  useEffect(() => {
    const key = `${showAdminStoreUI}|${String(effectiveHighest ?? "")}|${String(
      highest_role ?? ""
    )}`
    if (storeRoleBootstrapKey.current === key) return
    storeRoleBootstrapKey.current = key

    if (showAdminStoreUI) {
      if (selectedStatus === "approved") {
        fetchApprovedStore("", "Name", "", "", 1)
      } else {
        const requestStatus =
          selectedStatus === "pending" ? 1 : REJECTED_REQUEST_STATUS
        fetchOtherStores(requestStatus, null, "Name", "", 1)
      }
    } else if (highest_role === "OWNER") {
      fetchApprovedStore("", "Name", "", "", 1)
    }
  }, [
    showAdminStoreUI,
    effectiveHighest,
    highest_role,
    selectedStatus,
    fetchApprovedStore,
    fetchOtherStores,
  ])

  // Handle merchant change with proper guards
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }

    if (selectMerchant) {
      fetchStoresBasedOnStatus()
    }
  }, [selectMerchant])

  // Create a stable fetchStores function - will fetch all stores if no merchant is selected
  const fetchStoresBasedOnStatus = useCallback(() => {
    // Prevent concurrent fetches
    if (fetchInProgress.current) return
    fetchInProgress.current = true

    try {
      if (selectedStatus === "approved") {
        const status = filterStatus === "All" ? "" : filterStatus
        fetchApprovedStore(
          selectMerchant,
          selectedCategory,
          searchTerm,
          status,
          pagination.current
        )
      } else {
        // For pending, send status 1; for rejected, send both rejected (3) and permanently rejected (5)
        const requestStatus =
          selectedStatus === "pending" ? 1 : REJECTED_REQUEST_STATUS
        fetchOtherStores(
          requestStatus,
          selectMerchant || null,
          selectedCategory,
          searchTerm,
          pagination.current
        )
      }
    } finally {
      fetchInProgress.current = false
    }
  }, [
    fetchApprovedStore,
    fetchOtherStores,
    filterStatus,
    selectMerchant,
    selectedCategory,
    selectedStatus,
    searchTerm,
  ])

  // Handle search and filter changes with debounce
  useEffect(() => {
    if (isInitialMount.current) return

    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current)
    }

    searchTimeout.current = setTimeout(() => {
      // Reset to page 1 when searching/filtering
      setPagination((prev) => ({ ...prev, current: 1 }))
      // Always fetch data, whether a merchant is selected or not
      // Use page 1 after reset
      if (selectedStatus === "approved") {
        const status = filterStatus === "All" ? "" : filterStatus
        fetchApprovedStore(
          selectMerchant,
          selectedCategory,
          searchTerm,
          status,
          1
        )
      } else {
        // For pending, send status 1; for rejected, send both rejected (3) and permanently rejected (5)
        const requestStatus =
          selectedStatus === "pending" ? 1 : REJECTED_REQUEST_STATUS
        fetchOtherStores(
          requestStatus,
          selectMerchant || null,
          selectedCategory,
          searchTerm,
          1
        )
      }
    }, 300)

    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current)
      }
    }
  }, [
    searchTerm,
    filterStatus,
    selectedCategory,
    selectedStatus,
    selectMerchant,
    fetchApprovedStore,
    fetchOtherStores,
  ])

  // Handle status change
  const handleStatusChange = useCallback(
    (status) => {
      setSelectedStatus(status)

      // Apply saved defaults for this status tab
      const statusDefault = statusDefaults[status]
      setSelectedCategory(statusDefault.category)

      // Reset search term
      setSearchTerm("")

      // Reset to page 1 when status changes
      setPagination((prev) => ({ ...prev, current: 1 }))

      if (status === "approved") {
        setFilterStatus(statusDefault.filter || "All")
        const filterValue =
          statusDefault.filter === "All" ? "" : statusDefault.filter

        // Always fetch, whether a merchant is selected or not
        fetchApprovedStore(
          selectMerchant || "",
          statusDefault.category,
          "",
          filterValue,
          1
        )
      } else {
        setFilterStatus("")
        // For pending, send status 1; for rejected, send both rejected (3) and permanently rejected (5)
        const requestStatus =
          status === "pending" ? 1 : REJECTED_REQUEST_STATUS

        // Always fetch, whether a merchant is selected or not
        // Pass null instead of empty string if no merchant selected
        fetchOtherStores(
          requestStatus,
          selectMerchant || null,
          statusDefault.category,
          "",
          1
        )
      }
    },
    [fetchApprovedStore, fetchOtherStores, statusDefaults, selectMerchant]
  )

  // Handle category change with state memory
  const handleCategoryTypeChange = useCallback(
    (value) => {
      setSearchTerm("")
      setSelectedCategory(value)

      // Save this selection in the defaults for current status
      setStatusDefaults((prev) => ({
        ...prev,
        [selectedStatus]: {
          ...prev[selectedStatus],
          category: value,
        },
      }))

      // Reset to page 1 when category changes
      setPagination((prev) => ({ ...prev, current: 1 }))

      if (selectedStatus === "approved") {
        const status = filterStatus === "All" ? "" : filterStatus
        fetchApprovedStore(selectMerchant, value, "", status, 1)
      } else {
        // For pending, send status 1; for rejected, send both rejected (3) and permanently rejected (5)
        const requestStatus =
          selectedStatus === "pending" ? 1 : REJECTED_REQUEST_STATUS
        fetchOtherStores(requestStatus, selectMerchant || null, value, "", 1)
      }
    },
    [
      selectedStatus,
      fetchApprovedStore,
      fetchOtherStores,
      filterStatus,
      selectMerchant,
    ]
  )

  // Handle status filter change with state memory
  const handleStatusFilterChange = useCallback(
    (value) => {
      setFilterStatus(value)

      // Save this selection in the defaults for current status
      setStatusDefaults((prev) => ({
        ...prev,
        [selectedStatus]: {
          ...prev[selectedStatus],
          filter: value,
        },
      }))
    },
    [selectedStatus]
  )

  const handleTypeChange = useCallback((value) => {
    setSearchTerm(value || "")
  }, [])

  // Handle search input change
  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value)
  }, [])

  // Reset search with defaults
  const resetSearch = useCallback(() => {
    // Reset to defaults for current status
    const statusDefault = statusDefaults[selectedStatus]
    setSelectedCategory(statusDefault.category)
    setSearchTerm("")
    if (selectedStatus === "approved") {
      setFilterStatus(statusDefault.filter || "All")
    } else {
      setFilterStatus("")
    }
  }, [selectedStatus, statusDefaults])

  // Modal handlers - memoized to prevent recreations
  const showModal = useCallback((row) => {
    setIsModalOpen(true)
    setRejectedRequestId(row.id)
    // Permanently rejected (5) requests are closed — no new comments allowed
    setIsRejectedPermanently(row.request_status === 5)
  }, [])

  const handleCancel = useCallback(() => {
    setIsModalOpen(false)
  }, [])

  const handleProductOpenModal = useCallback((row) => {
    setProductModal(true)
    setSelectedStore(row)
  }, [])

  const handleProductCloseModal = useCallback(() => {
    setProductModal(false)
    setSelectedStore({})
  }, [])

  const handleRequestOpenModal = useCallback((row) => {
    setRequestModal(true)
    setSelectedStore(row)
  }, [])

  const handleRequestCloseModal = useCallback(() => {
    setRequestModal(false)
  }, [])

  // Delete confirmation handlers
  const handleConfirmDelete = useCallback(() => {
    if (!canDelete) {
      message.error(
        translations[lang]?.common_error_msg?.delete_not_allowed ||
          "Your role does not allow deleting in this section."
      )
      return
    }
    if (isRequestDelete) {
      handleDeleteRequest(storeToDelete?.id)
    } else {
      handleDelete(storeToDelete?.id)
    }
  }, [
    canDelete,
    lang,
    handleDelete,
    handleDeleteRequest,
    isRequestDelete,
    storeToDelete?.id,
  ])

  const handleCancelDelete = useCallback(() => {
    setDeleteModalVisible(false)
    setStoreToDelete(null)
  }, [])

  return (
    <div>
      <StoreResubmissionChat
        RejectedId={rejectedRequestId}
        isModalOpen={isModalOpen}
        handleCancel={handleCancel}
        isPermanentlyRejected={isRejectedPermanently}
      />

      {showAdminStoreUI && productModal && selectedStore && (
        <StoreProductViewModal
          selectedStore={selectedStore}
          visible={productModal}
          onClose={handleProductCloseModal}
        />
      )}

      {showAdminStoreUI && (
        <StoreVIewDetailsModal
          setStores={setStores}
          selectedStatus={selectedStatus}
          selectedStore={selectedStore}
          visible={requestModal}
          onClose={handleRequestCloseModal}
          isReadOnly={isReadOnly}
        />
      )}
      <DeleteConfirmationModal
        deleteLoading={deleteStore}
        visible={deleteModalVisible}
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title={modal?.confirm_delete_text}
        content={
          <>
            {modal?.description_1}{" "}
            <b>
              {" "}
              {lang === "ar"
                ? storeToDelete?.store_name_arabic
                : storeToDelete?.store_name}
            </b>{" "}
            {modal?.description_2}
          </>
        }
      />

      <div>
        <PageHeader
          user={user}
          title={currentLanguage?.Store}
          buttonText={
            // Super Admin: no Select Merchant (lists all by default)
            // Owner / Admin: Add Store only — never Select Merchant
            effectiveHighest === "SUPERADMIN"
              ? ""
              : highest_role === "OWNER" || highest_role === "ADMIN"
                ? currentLanguage?.add_store
                : ""
          }
          buttonPath={
            isReadOnly || showAdminStoreUI
              ? ""
              : highest_role === "OWNER" || highest_role === "ADMIN"
                ? "/stores/create-store"
                : ""
          }
          onClick={undefined}
        />

        <div className="main-content-container">
          {(user?.store_data || isReadOnly || showAdminStoreUI) ? (
            <>
              <div className="d-flex mb-24 align-items-center">
                <div
                  className={`p-12 mr-8 pointer ${
                    selectedStatus === "approved" && "selected-store-type"
                  }`}
                  onClick={() => handleStatusChange("approved")}
                >
                  {currentLanguage?.approved}
                </div>
                <div
                  className={`p-12 mr-8 pointer ${
                    selectedStatus === "pending" && "selected-store-type"
                  }`}
                  onClick={() => handleStatusChange("pending")}
                >
                  {currentLanguage?.pending}
                </div>
                <div
                  className={`p-12 pointer ${
                    selectedStatus === "rejected" && "selected-store-type"
                  }`}
                  onClick={() => handleStatusChange("rejected")}
                >
                  {currentLanguage?.rejected}
                </div>
              </div>

              <div className="d-flex mb-24 align-items-center">
                <Dropdown
                  selectedCategory={selectedCategory}
                  label={filters?.select}
                  iconSrc="/icons/selectstoreanalytics.svg"
                  options={[
                    { label: currentLanguage?.dropdown_1?.name, value: "Name" },
                    { label: currentLanguage?.dropdown_1?.type, value: "Type" },
                  ]}
                  onSelect={handleCategoryTypeChange}
                />
                <div className={lang === "ar" ? "ml-10" : "mr-10"}>
                  {selectedCategory === "Type" ? (
                    <>
                      <Select
                        style={{
                          width: "100%",
                          minWidth: "249px",
                          marginRight: lang === "ar" ? "0px" : "12px",
                          marginLeft: lang === "ar" ? "12px" : "0px",
                          alignItems: "center",
                        }}
                        className="product-search"
                        showSearch
                        placeholder={filters?.select_categories}
                        allowClear
                        value={searchTerm || undefined}
                        onChange={handleTypeChange}
                        options={[
                          {
                            value: "ZID",
                            label: currentLanguage?.platformOptions?.ZID,
                          },
                          {
                            value: "SALLA",
                            label: currentLanguage?.platformOptions?.SALLA,
                          },
                          {
                            value: "MAGENTO",
                            label: currentLanguage?.platformOptions?.MAGENTO,
                          },
                          {
                            value: "SHOPIFY",
                            label: currentLanguage?.platformOptions?.SHOPIFY,
                          },
                          {
                            value: "Other",
                            label: currentLanguage?.platformOptions?.Other,
                          },
                        ]}
                      />
                    </>
                  ) : (
                    <>
                      <input
                        type="search"
                        className="search-store"
                        placeholder={filters?.search}
                        value={searchTerm}
                        onChange={handleSearchChange}
                      />
                    </>
                  )}
                </div>
                {selectedStatus === "approved" && (
                  <Dropdown
                    selectedCategory={filterStatus}
                    label={filters?.all}
                    iconSrc="/icons/selectstoreanalytics.svg"
                    options={[
                      { label: currentLanguage?.dropdown_2?.all, value: "All" },
                      {
                        label: currentLanguage?.dropdown_2?.active,
                        value: "Active",
                      },
                      {
                        label: currentLanguage?.dropdown_2?.inactive,
                        value: "Inactive",
                      },
                    ]}
                    onSelect={handleStatusFilterChange}
                  />
                )}
              </div>

              {loading ? (
                <div className="w-100 h-100 d-flex justify-content-center align-items-center">
                  <Spin size="large" />
                </div>
              ) : stores && Array.isArray(stores) && stores.length > 0 ? (
                <>
                  <Table
                    columns={getStoreColumns(
                      selectedStatus,
                      effectiveHighest,
                      market_role
                    )}
                    data={stores}
                    actions={
                      isReadOnly
                        ? getReadOnlyStoreActions(
                            selectedStatus,
                            handleProductOpenModal,
                            handleRequestOpenModal,
                            showModal,
                            currentLanguage,
                            readOnlyActionHint
                          )
                        : effectiveHighest === "OWNER" ||
                            effectiveHighest === "ADMIN" ||
                            effectiveHighest === "SUPERADMIN"
                          ? getStoreActions(
                              selectedStatus,
                              handleDelete,
                              navigate,
                              handleDeleteRequest,
                              showModal,
                              effectiveHighest,
                              handleProductOpenModal,
                              handleRequestOpenModal,
                              setDeleteModalVisible,
                              setStoreToDelete,
                              setIsRequestDelete,
                              lang,
                              currentLanguage,
                              canDelete
                            )
                          : effectiveHighest === "VIEWER"
                            ? viewOnlyStoreActions(
                                selectedStatus,
                                showModal,
                                currentLanguage,
                                readOnlyActionHint
                              )
                            : []
                    }
                  />
                  {pagination.total > 0 && (
                    <div className="d-flex justify-content-end mt-24">
                      <Pagination
                        current={pagination.current}
                        pageSize={pagination.pageSize}
                        total={pagination.total}
                        showSizeChanger={false}
                        showTotal={(total, range) =>
                          `${range[0]}-${range[1]} of ${total}`
                        }
                        onChange={(page) => {
                          if (selectedStatus === "approved") {
                            const status =
                              filterStatus === "All" ? "" : filterStatus
                            fetchApprovedStore(
                              selectMerchant,
                              selectedCategory,
                              searchTerm,
                              status,
                              page
                            )
                          } else {
                            // For pending, send status 1; for rejected, send both rejected (3) and permanently rejected (5)
                            const requestStatus =
                              selectedStatus === "pending"
                                ? 1
                                : REJECTED_REQUEST_STATUS
                            fetchOtherStores(
                              requestStatus,
                              selectMerchant || null,
                              selectedCategory,
                              searchTerm,
                              page
                            )
                          }
                        }}
                      />
                    </div>
                  )}
                </>
              ) : (
                <EmptyState
                  title={currentLanguage?.no_store?.title}
                  description={
                    selectedStatus === "approved"
                      ? currentLanguage?.no_store?.description_approved
                      : selectedStatus === "pending"
                      ? currentLanguage?.no_store?.description_pending
                      : currentLanguage?.no_store?.description_rejected
                  }
                  buttonText={
                    !showAdminStoreUI && highest_role === "OWNER"
                      ? currentLanguage?.add_store
                      : undefined
                  }
                  buttonPath={
                    !showAdminStoreUI && highest_role === "OWNER"
                      ? "/stores/create-store"
                      : undefined
                  }
                />
              )}
            </>
          ) : (
            <EmptyState
              title={currentLanguage?.no_store?.description_approved}
              description={currentLanguage?.no_store?.main_description}
              buttonPath="/stores/create-store"
            />
          )}
        </div>
      </div>

      <StoreDrawer
        highest_role={highest_role}
        effectiveHighest={effectiveHighest}
        market_role={market_role}
        title={currentLanguage?.select_merchant}
        visible={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        stores={merchants}
        selectMerchant={selectMerchant}
        setSelectMerchant={setSelectMerchant}
        AllFilterOption={currentLanguage?.Merchants}
      />
    </div>
  )
}

export default Stores
